import React, { useState } from 'react';
import { loginUser } from '../services/userService';
import { initiateOidcLogin, getOidcConfig } from '../services/oidcService';
import { UserLogin } from '../types/user';
import '../styles/Login.css';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton } from '@mui/material';

// Import Kenya coat of arms logo
import kenyaArmsLogo from '../assets/kenya_coat_of_arms.png';

interface LoginProps {
  onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [formData, setFormData] = useState<UserLogin>({
    email: '',
    password: ''
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [oidcLoading, setOidcLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const oidcEnabled = Boolean(getOidcConfig());

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOidcLogin = async () => {
    setError('');
    setOidcLoading(true);
    try {
      await initiateOidcLogin();
    } catch (err: any) {
      console.error('OIDC login initiation failed:', err);
      setError(err.message || 'Failed to start CHW Registry sign in.');
      setOidcLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      
      // Attempt to login with provided credentials
      await loginUser(formData);
      onLogin(); // Call the parent component's onLogin function to update app state
    } catch (err: any) {
      console.error('Login failed:', err);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <div className="kenya-logo-container">
            <img src={kenyaArmsLogo} alt="Kenya Coat of Arms" className="kenya-arms-logo" />
          </div>
          <h2>CQI Dashboard v3.0</h2>
          <h3>Please sign in</h3>
          {error && <div className="error-message">{error}</div>}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="text"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
          <div className="form-group password-field">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
            />
            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              edge="end"
              aria-label="toggle password visibility"
              className="password-toggle"
              disabled={loading}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </div>
          <button 
            type="submit" 
            className="login-button" 
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
        {oidcEnabled && (
          <>
            <div style={{ margin: '1rem 0', textAlign: 'center', color: '#666', fontSize: '0.9rem' }}>
              — or —
            </div>
            <button
              type="button"
              className="login-button"
              style={{ backgroundColor: '#1a5f7a' }}
              onClick={handleOidcLogin}
              disabled={oidcLoading}
            >
              {oidcLoading ? 'Redirecting...' : 'Sign in with CHW Registry'}
            </button>
          </>
        )}
        <div className="login-footer">
          © 2020 - 2025 | CQI Kenya
        </div>
      </div>
    </div>
  );
};

export default Login;
